import {
    addDays,
    addMonths,
    addWeeks,
    addYears,
    eachDayOfInterval,
    endOfMonth,
    endOfWeek,
    endOfYear,
    isBefore,
    isSameDay,
    startOfDay,
    startOfMonth,
    startOfWeek,
    startOfYear
} from "date-fns";
import type React from "react";
import {memo, useCallback, useMemo, useRef, useState} from "react";
import type {DateType} from "react-native-ui-datepicker";

import {useView} from "../../hooks/useView";
import {ChevronDownIcon} from "../../react/components/Icons/ChevronDownIcon";
import useDisclose from "../../react/hooks/useDisclose";
import {formatMMddyyyy} from "../../utils/date";
import {Modal} from "../Modal";
import Popover from "../Popover";
import {Box} from "../ui/box";
import {Button, ButtonText} from "../ui/button";
import {Center} from "../ui/center";
import {HStack} from "../ui/hstack";
import {Input, InputField, InputIcon} from "../ui/input";
import {Pressable} from "../ui/pressable";
import {Text} from "../ui/text";
import {VStack} from "../ui/vstack";

import DateRangePickerUI from "./DateRangePickerUI";
import QuickSelectDateButton from "./QuickSelectDateButton";
import type {QuickSelectDate} from "./type";
import {formatDateRange} from "./util";

export type OptionalDate = Date | undefined;

export type RenderHandlerDateRangePickerProps = {
    startDate: OptionalDate;
    endDate: OptionalDate;
    onOpen: () => void;
    onClose: () => void;
};

export type Props = {
    allowQuickSelectDate?: boolean;
    defaultOpen?: boolean;
    allowSameDay?: boolean;
    startDate?: OptionalDate;
    endDate?: OptionalDate;
    disabledDates?: (date: DateType) => boolean;
    onSelectedDates?: (startDate: OptionalDate, endDate: OptionalDate) => void;
    onClose?: () => void;
    renderHandler?: (params: RenderHandlerDateRangePickerProps) => React.ReactNode;
};

const DATE_PICKER_HEIGHT = 525;

const SelectDateRangePicker: React.FC<Props> = ({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    disabledDates = [],
    allowQuickSelectDate,
    defaultOpen = false,
    allowSameDay,
    onSelectedDates,
    onClose: onCloseProp,
    renderHandler
}) => {
    const view = useView();
    const today = useMemo(() => new Date(), []);
    const [startDate, setStartDate] = useState<OptionalDate>(defaultStartDate);
    const [endDate, setEndDate] = useState<OptionalDate>(defaultEndDate);
    const {isOpen, onClose, onOpen} = useDisclose(defaultOpen);
    const [enteredTo, setEnteredTo] = useState<OptionalDate>(undefined);
    const alreadySelectStartDate = useRef(false);

    const selectedDays = useMemo(
        () => ({from: startDate, to: enteredTo || endDate}),
        [endDate, enteredTo, startDate]
    );

    const isSelectingFirstDay = useCallback(
        (day: Date, startDate?: OptionalDate, endDate?: OptionalDate) => {
            const isBeforeFirstDay = startDate && isBefore(day, startDate);
            const isRangeSelected = startDate && endDate;
            return !startDate || isBeforeFirstDay || isRangeSelected;
        },
        []
    );

    const isValidRange = useCallback(
        (start: Date, end: Date) => {
            if (!disabledDates) return true;

            if (typeof disabledDates === "function") {
                const daysInRange = eachDayOfInterval({start, end});
                return !daysInRange.some(date => disabledDates(date));
            }

            return true;
        },
        [disabledDates]
    );

    const handleClose = useCallback(() => {
        setStartDate(defaultStartDate);
        setEndDate(defaultEndDate);
        setEnteredTo(undefined);
        alreadySelectStartDate.current = false;
        onCloseProp?.();
        onClose();
    }, [defaultEndDate, defaultStartDate, onClose, onCloseProp]);

    const quickSelectDates = useMemo(() => {
        const dates = [
            {name: "Today", startDate: today, endDate: today},
            {
                name: "Yesterday",
                startDate: startOfDay(addDays(today, -1)),
                endDate: startOfDay(addDays(today, -1))
            },
            {
                name: "This Week",
                startDate: startOfWeek(today),
                endDate: endOfWeek(today)
            },
            {
                name: "Last week",
                startDate: startOfWeek(addWeeks(today, -1)),
                endDate: endOfWeek(addWeeks(today, -1))
            },
            {
                name: "This Month",
                startDate: startOfMonth(today),
                endDate: endOfMonth(today)
            },
            {
                name: "Last Month",
                startDate: startOfMonth(addMonths(today, -1)),
                endDate: endOfMonth(addMonths(today, -1))
            },
            {
                name: "This Year",
                startDate: startOfYear(today),
                endDate: endOfYear(today)
            },
            {
                name: "Last Year",
                startDate: startOfYear(addYears(today, -1)),
                endDate: endOfYear(addYears(today, -1))
            }
        ];
        return dates.filter(date => isValidRange(date.startDate, date.endDate));
    }, [isValidRange, today]);

    const handleQuickSelect = useCallback(
        (day: QuickSelectDate) => {
            setStartDate(day.startDate);
            setEndDate(day.endDate);
            onSelectedDates?.(day.startDate, day.endDate);
            onClose();
        },
        [onClose, onSelectedDates]
    );

    const handleDayClick = useCallback(
        (day: Date) => {
            const startDay = day;

            // Cancel selection
            if (
                startDate &&
                endDate &&
                isSameDay(startDay, startDate) &&
                (isSameDay(startDate, endDate) || !allowSameDay)
            ) {
                setStartDate(undefined);
                setEndDate(undefined);
                return;
            }

            if (
                isSelectingFirstDay(startDay, startDate, endDate) &&
                !alreadySelectStartDate.current
            ) {
                const endDay = endDate && isBefore(startDay, endDate) ? endDate : undefined;
                const valid = isValidRange(startDay, endDay || addDays(startDay, 1));

                setStartDate(valid ? startDay : undefined);
                setEndDate(valid ? endDay : undefined);
                setEnteredTo(undefined);

                alreadySelectStartDate.current = true;
            } else if (startDate && (allowSameDay || !isSameDay(startDate, startDay))) {
                const isDayValid =
                    isBefore(startDate, day) || (allowSameDay && isSameDay(startDate, day));

                const newStartDay = isDayValid ? startDate : day;
                const endDay = isDayValid ? day : undefined;
                const valid = isValidRange(newStartDay, endDay || addDays(newStartDay, 1));

                setStartDate(valid ? newStartDay : undefined);
                setEndDate(valid ? endDay : undefined);
                setEnteredTo(undefined);

                alreadySelectStartDate.current = false;
            }
        },
        [allowSameDay, endDate, isSelectingFirstDay, isValidRange, startDate]
    );

    const handleDone = useCallback(() => {
        onSelectedDates?.(startDate, endDate);
        onClose();
    }, [endDate, onClose, onSelectedDates, startDate]);

    const handleDayMouseEnter = useCallback(
        (day: Date) => {
            const startDay = day;

            if (
                startDate &&
                !isSelectingFirstDay(startDay, startDate, endDate) &&
                isValidRange(startDate, startDay)
            ) {
                setEnteredTo(startDay);
            }
        },
        [endDate, isSelectingFirstDay, isValidRange, startDate]
    );

    const triggerElement = useMemo(() => {
        const startDate = selectedDays.from;
        const endDate = selectedDays.to;
        if (renderHandler) {
            return renderHandler({
                startDate,
                endDate,
                onClose,
                onOpen
            });
        }

        return (
            <Pressable testID="input-trigger" onPress={onOpen}>
                <Input isReadOnly className="md:w-64">
                    <InputField
                        value={`Date range: ${
                            startDate && endDate ? formatDateRange(startDate, endDate) : ""
                        }`}
                    />
                    <InputIcon as={ChevronDownIcon} />
                </Input>
            </Pressable>
        );
    }, [onClose, onOpen, renderHandler, selectedDays]);

    const dateRangePickerElement = (
        <DateRangePickerUI
            disabledDates={disabledDates}
            endDate={selectedDays.to}
            mode="range"
            startDate={selectedDays.from}
            onMouseEnter={handleDayMouseEnter}
            onSelectDay={handleDayClick}
        />
    );

    if (view === "native" || view === "webMobile") {
        return (
            <>
                {process.env.IS_WEB && triggerElement}
                <Modal
                    btnTextOk="Done"
                    isOpen={isOpen}
                    title="Choose Date"
                    onClose={handleClose}
                    onSubmit={handleDone}
                >
                    <VStack space="md">
                        {allowQuickSelectDate && quickSelectDates.length > 0 && (
                            <VStack className="w-full mb-4" space="sm">
                                {quickSelectDates.map(day => (
                                    <QuickSelectDateButton
                                        key={day.name}
                                        day={day}
                                        onQuickSelectDate={handleQuickSelect}
                                    />
                                ))}
                            </VStack>
                        )}
                        <Center className="flex-1">{dateRangePickerElement}</Center>
                        <HStack
                            className="items-center py-4 px-5 border-t border-solid border-blueGray-200"
                            space="sm"
                        >
                            <Box className="flex-1">
                                <Input isReadOnly>
                                    <InputField
                                        value={startDate ? formatMMddyyyy(startDate) : ""}
                                    />
                                </Input>
                            </Box>
                            <Box className="flex-1">
                                <Input isReadOnly>
                                    <InputField value={endDate ? formatMMddyyyy(endDate) : ""} />
                                </Input>
                            </Box>
                        </HStack>
                    </VStack>
                </Modal>
            </>
        );
    }

    return (
        <Popover
            contentHeight={DATE_PICKER_HEIGHT}
            isOpen={isOpen}
            placement="bottom"
            trigger={triggerElement}
            onClose={handleClose}
        >
            <VStack space="lg">
                <HStack space="xl">
                    {allowQuickSelectDate && quickSelectDates.length > 0 && (
                        <VStack space="sm">
                            {quickSelectDates.map(day => (
                                <QuickSelectDateButton
                                    key={day.name}
                                    day={day}
                                    onQuickSelectDate={handleQuickSelect}
                                />
                            ))}
                        </VStack>
                    )}
                    <Center className="flex-1">{dateRangePickerElement}</Center>
                </HStack>
                <VStack className="items-end" space="lg">
                    <HStack className="items-center w-full" space="md">
                        <Input isReadOnly className="flex-1">
                            <InputField value={startDate ? formatMMddyyyy(startDate) : ""} />
                        </Input>
                        <Text color="blueGray.400" variant="sm">
                            -
                        </Text>
                        <Input isReadOnly className="flex-1">
                            <InputField value={endDate ? formatMMddyyyy(endDate) : ""} />
                        </Input>
                    </HStack>
                    <Button onPress={handleDone}>
                        <ButtonText>Done</ButtonText>
                    </Button>
                </VStack>
            </VStack>
        </Popover>
    );
};

export default memo(SelectDateRangePicker);
